import { MarginHighlightType } from '../constants/marginTracker';
import {
  GeneralAssessment,
  ScopeTimelineAssessment,
} from '../features/globalTracker/types';
import { IdField, TimestampsFields } from './generic';
import { QuarterlyRevenueAccountSummary } from './quarterlyRevenue';
import { UserWithPermissions } from './user';

// --- --- --- --- --- Generic Types --- --- --- --- ---

export enum Seniority {
  JUNIOR = 'Junior',
  JUNIOR_1 = 'Junior 1',
  JUNIOR_2 = 'Junior 2',
  MIDDLE = 'Middle',
  MIDDLE_1 = 'Middle 1',
  MIDDLE_2 = 'Middle 2',
  SENIOR = 'Senior',
  SENIOR_1 = 'Senior 1',
  SENIOR_2 = 'Senior 2',
  LEAD = 'Lead',
  TECH_LEAD = 'Tech Lead', // Temporary. Tech Lead should be removed, since it's the same as Lead, just alias for Developers
  DM = 'DM',
}

export const seniorityValues = Object.values(Seniority);

export enum Stream {
  AUTOMATION_QA = 'Automation QA',
  DESIGNER = 'Designer',
  DEVELOPER = 'Developer',
  DEVOPS = 'DevOps',
  MANUAL_QA = 'Manual QA',
  PM = 'PM',
  SECURITY_ENGINEER = 'Security Engineer',
  SALES_DEVELOPMENT_REPRESENTATIVE = 'Sales Development Representative',
  DATA_SCIENCE_SPECIALIST = 'Data Science Specialist',
  SOLUTION_ARCHITECT = 'Solution Architect',
  DQ_ANALYST = 'DQ Analyst',
  COPYWRITER = 'Copywriter',
}

export const streamValues = Object.values(Stream);

export const streamsLabels = {
  [Stream.AUTOMATION_QA]: 'Automation QA',
  [Stream.DESIGNER]: 'Designer',
  [Stream.DEVELOPER]: 'Developer',
  [Stream.DEVOPS]: 'DevOps',
  [Stream.MANUAL_QA]: 'Manual QA',
  [Stream.PM]: 'PM',
  [Stream.SECURITY_ENGINEER]: 'Security Engineer',
  [Stream.SALES_DEVELOPMENT_REPRESENTATIVE]: 'Sales Development Representative',
  [Stream.DATA_SCIENCE_SPECIALIST]: 'Data Science Specialist',
  [Stream.SOLUTION_ARCHITECT]: 'Solution Architect',
  [Stream.DQ_ANALYST]: 'DQ Analyst',
  [Stream.COPYWRITER]: 'Copywriter',
};

export const getStreamFromLabel = (label: string) => {
  return Object.values(Stream).find(
    (stream) => streamsLabels[stream] === label,
  );
};

// Stream is used in Account Tracker for fetching compensation ranges. SowStream and Stream are supposed to be the same,
// but just in case I will keep them separate, until we 100% sync with stakeholders that they are supposed to be the same.
export enum SowStream {
  DEVELOPER = 'Developer',
  DEVOPS = 'DevOps',
  SECURITY_ENGINEER = 'Security Engineer',
  AUTOMATION_QA_ENGINEER = 'Automation QA Engineer',
  MANUAL_QA_ENGINEER = 'Manual QA Engineer',
  DESIGNER = 'Designer',
  PM = 'PM',

  // Only in SowStream
  SALES_DEVELOPMENT_REPRESENTATIVE = 'Sales Development Representative',
  DATA_SCIENCE_SPECIALIST = 'Data Science Specialist',
  SOLUTION_ARCHITECT = 'Solution Architect',
  DQ_ANALYST = 'DQ Analyst',
  COPYWRITER = 'Copywriter',
  SCRUM_MASTER = 'Scrum Master',
  PRODUCT_OWNER = 'Product Owner',
}

export const sowStreamValues = Object.values(SowStream);

export const sowStreamsLabels = {
  [SowStream.DEVELOPER]: 'Developer',
  [SowStream.DEVOPS]: 'DevOps',
  [SowStream.SECURITY_ENGINEER]: 'Security Engineer',
  [SowStream.AUTOMATION_QA_ENGINEER]: 'Automation QA Engineer',
  [SowStream.MANUAL_QA_ENGINEER]: 'Manual QA Engineer',
  [SowStream.DESIGNER]: 'Designer',
  [SowStream.PM]: 'PM',

  // Only in SowStream
  [SowStream.SALES_DEVELOPMENT_REPRESENTATIVE]:
    'Sales Development Representative',
  [SowStream.DATA_SCIENCE_SPECIALIST]: 'Data Science Specialist',
  [SowStream.SOLUTION_ARCHITECT]: 'Solution Architect',
  [SowStream.DQ_ANALYST]: 'DQ Analyst',
  [SowStream.COPYWRITER]: 'Copywriter',
  [SowStream.SCRUM_MASTER]: 'Scrum Master',
  [SowStream.PRODUCT_OWNER]: 'Product Owner',
};

export const getSowStreamFromLabel = (label: string) => {
  return Object.values(SowStream).find(
    (stream) => sowStreamsLabels[stream] === label,
  );
};

export const getSowSeniorities = (stream?: SowStream): Seniority[] => {
  if (!stream) return [];

  const streamSeniorityMap = [
    {
      streams: [
        SowStream.SECURITY_ENGINEER,
        SowStream.DEVELOPER,
        SowStream.DEVOPS,
      ],
      seniorities: [
        Seniority.JUNIOR_1,
        Seniority.JUNIOR_2,
        Seniority.MIDDLE_1,
        Seniority.MIDDLE_2,
        Seniority.SENIOR_1,
        Seniority.SENIOR_2,
        Seniority.TECH_LEAD,
      ],
    },
    {
      streams: [
        SowStream.AUTOMATION_QA_ENGINEER,
        SowStream.MANUAL_QA_ENGINEER,
        SowStream.SALES_DEVELOPMENT_REPRESENTATIVE,
        SowStream.DATA_SCIENCE_SPECIALIST,
      ],
      seniorities: [
        Seniority.JUNIOR_1,
        Seniority.JUNIOR_2,
        Seniority.MIDDLE_1,
        Seniority.MIDDLE_2,
        Seniority.SENIOR_1,
        Seniority.SENIOR_2,
        Seniority.LEAD,
      ],
    },
    {
      streams: [
        SowStream.SOLUTION_ARCHITECT,
        SowStream.DQ_ANALYST,
        SowStream.COPYWRITER,
        SowStream.PM,
        SowStream.DESIGNER,
        SowStream.SCRUM_MASTER,
        SowStream.PRODUCT_OWNER,
      ],
      seniorities: [
        Seniority.JUNIOR,
        Seniority.MIDDLE,
        Seniority.SENIOR,
        Seniority.LEAD,
      ],
    },
  ];

  return (
    streamSeniorityMap.find((map) => map.streams.includes(stream))
      ?.seniorities ?? []
  );
};

export enum LegalLocation {
  INDIA = 'India',
  MEXICO = 'Mexico',
  UKRAINE = 'Ukraine',
  UNITED_STATES = 'United States',
  GUATEMALA = 'Guatemala',
  COLOMBIA = 'Colombia',
  BRAZIL = 'Brazil',
  ARGENTINA = 'Argentina',
  PORTUGAL = 'Portugal',
  POLAND = 'Poland',
  REMOTE = 'Remote',
}

export enum QuarterlyRevenueLocation {
  ARGENTINA = 'Argentina',
  ARMENIA = 'Armenia',
  BRAZIL = 'Brazil',
  BULGARIA = 'Bulgaria',
  COLOMBIA = 'Colombia',
  GEORGIA = 'Georgia',
  GUATEMALA = 'Guatemala',
  INDIA = 'India',
  KAZAKHSTAN = 'Kazakhstan',
  MEXICO = 'Mexico',
  POLAND = 'Poland',
  PORTUGAL = 'Portugal',
  REMOTE = 'Remote',
  ROMANIA = 'Romania',
  SLOVAKIA = 'Slovakia',
  SLOVENIA = 'Slovenia',
  SPAIN = 'Spain',
  USA = 'USA',
  UKRAINE = 'Ukraine',
  UNITED_KINGDOM = 'United Kingdom',
  UZBEKISTAN = 'Uzbekistan',
  VIETNAM = 'Vietnam',
}

export enum QuarterlyRevenueSeniorities {
  TRAINEE = 'Trainee',
  JUNIOR = 'Junior',
  JUNIOR_1 = 'Junior 1',
  JUNIOR_2 = 'Junior 2',
  MIDDLE = 'Middle',
  MIDDLE_1 = 'Middle 1',
  MIDDLE_2 = 'Middle 2',
  SENIOR = 'Senior',
  SENIOR_1 = 'Senior 1',
  SENIOR_2 = 'Senior 2',
  LEAD = 'Lead',
  DM = 'DM',
  ARCHITECT = 'Architect',
  C_LEVEL = 'C-level',
}

export const techFlowsList = [
  '.NET',
  '.NET / Angular',
  '.NET / React.js',
  '.NET / Vue.js',
  'AQA',
  'AQA / QA',
  'AQA C Sharp',
  'AQA Java',
  'AQA Java/Python',
  'AQA JavaScript',
  'AQA Python',
  'AQA Selenium',
  'Android',
  'Angular',
  'C Sharp / React.js',
  'C Sharp/Vue.js',
  'C#',
  'C++',
  'Data Engineer',
  'Data Engineer / Apache Spark',
  'Data Engineer Java',
  'Data Engineer Python',
  'Data Engineer Scala',
  'Data Scientist',
  'Data Scientist Analysis',
  'Data Scientist Generative AI',
  'Data Scientist Machine Learning',
  'DevOps',
  'DevOps / GCP',
  'DevOps AWS',
  'DevOps Azure',
  'Elixir',
  'Erlang',
  'Flutter',
  'Golang',
  'Golang / React.js',
  'Groovy',
  'Haskell',
  'Java',
  'Java / Angular',
  'Java / React.js',
  'Java/Vue.js',
  'JavaScript',
  'Kotlin',
  'Kotlin / React.js',
  'Node.js',
  'Node.js / Angular',
  'Node.js / React.js',
  'Node.js / Vue.js',
  'PHP',
  'PHP / JavaScript',
  'Perl',
  'Project Manager',
  'Python',
  'Python / Angular',
  'Python / Javascript',
  'Python / React.js',
  'Python / Vue.js',
  'React Native',
  'React.js',
  'Ruby',
  'Rust',
  'SDET-Mobile',
  'SQL',
  'Salesforce',
  'Salesforce',
  'Scala',
  'ServiceNow',
  'Shopify',
  'Swift',
  'Typescript',
  'Unity',
  'Vue.js',
  'XSLT',
  'iOS',
];

export enum EmploymentType {
  EMPLOYEE = 'Employee',
  CONTRACTOR = 'Contractor',
}

// --- --- --- --- --- Accounts Types --- --- --- --- ---
export interface MarginRange {
  min: number;
  max: number;
}

export interface MarginHighlight {
  id: string;
  min: number;
  max: number;
  type: MarginHighlightType;
}

export interface PortfolioMargins {
  grossMargin: number;
  netMargin: number;
  newGrossMargin: number;
  newNetMargin: number;
  pastRevenue?: number;
  pastRevenueCurrentYear?: number | null;
  currentYear?: number;
  projectedRevenue?: number;
}

export type ProjectedLogType =
  | 'FROM_INVOICE_DATE_TO_NOW'
  | 'FROM_HEADCOUNT_CHANGE_DATE_TO_HEADCOUNT_CHANGE_DATE'
  | 'FROM_HEADCOUNT_CHANGE_DATE_TO_END_OF_YEAR';

export interface ProjectedRevenuePayCostLog {
  type: ProjectedLogType;
  blendedBillRate: number;
  blendedPayRate: number;
  headcount: number;
  days: number;
  fromDate: string;
  toDate: string;
}

export interface ProjectedOperationalCostLog {
  type: ProjectedLogType;
  operationalCost: number;
  headcount: number;
  days: number;
  fromDate: string;
  toDate: string;
}

export interface BaseAccount {
  id: string;
  name: string;

  pastRevenue?: number | null;
  projectedRevenue?: number | null;
  projectedRevenueLogs?: ProjectedRevenuePayCostLog[] | null;
  revenueLastInvoiceDate?: string | null;

  grossMargin?: number | null;
  netMargin?: number | null;

  grossMarginTargets?: MarginRange;
  netMarginTargets?: MarginRange;
  portfolioMargins?: PortfolioMargins | null;

  sowLink?: string | null;
  status?: AccountStatus;
  grossMarginHighlights?: MarginHighlight[];
  netMarginHighlights?: MarginHighlight[];
  alert?: AlertType;
  initialRevenue?: number | null;
  latestQuarterlyRevenueStatus?: QuarterlyRevenueAccountSummary;
}

export interface RateCard {
  seniority: string;
  country: string;
  stream: string;
  billRate: number;
}

export interface CompensationRange {
  legalLocation: string;
  employmentType: string;
  seniority: string;
  currency: string;
  min: number;
  max: number;
  approvedMax: number;
}

export interface Member {
  id: string;
  email: string;
  fullName: string;
  seniority: Seniority;
  stream: Stream;
  legalLocation: LegalLocation;
  compensationRate?: number | null;
  companyRateCard?: RateCard;
  allocation?: number | null;
  compensationRange?: CompensationRange;
  grossMargin?: number;
  netMargin?: number;
  resource?: {
    billRate: number;
  };
  isHidden?: boolean | false;
  editable?: DummyMargin;
  projectId?: string;
  avatarUrl?: string;
  compensationType?: string | null;
  actualPayRate?: number | null;
}

export interface GroupedMembers {
  projectId?: string;
  groupName: string;
  members: Member[];
  grossMargin?: number | null;
  netMargin?: number | null;
  grossMarginHighlights?: MarginHighlight[];
  netMarginHighlights?: MarginHighlight[];
}

export interface DummyMember {
  id: string;
  seniority?: Seniority;
  stream?: Stream;
  country?: LegalLocation;
  compensationRate?: number;
  billRate?: number;
  grossMargin?: number;
  netMargin?: number;
  rateCard?: number;
  allocation: number;
  isHidden: boolean | false;
  payRange: [number, number];
  employmentType?: EmploymentType;
  projectId?: string;
}

export interface Project extends BaseAccount {
  members: Member[];
}

export interface Account extends BaseAccount {
  clientPartner?: BaseManager;
  portfolioDeliveryManager?: BaseManager;
  deliveryManager?: BaseManager;
}

export interface DummyMargin {
  netMargin?: number;
  grossMargin?: number;
  compensationRate?: number | null;
  billRate: number;
  allocation: number;
  isHidden: boolean;
}

export type MarginContractor = Pick<
  Member,
  'id' | 'compensationRate' | 'resource' | 'allocation' | 'projectId'
> &
  Partial<Pick<Member, 'legalLocation'>>;

export type MarginProject = Pick<Project, 'id'> & {
  contractors: MarginContractor[];
};

export type MarginResultContractor = Pick<
  Member,
  'id' | 'grossMargin' | 'netMargin' | 'projectId'
>;

export type MarginResultProject = Pick<
  Project,
  | 'id'
  | 'grossMargin'
  | 'netMargin'
  | 'grossMarginHighlights'
  | 'netMarginHighlights'
> & {
  contractors: MarginResultContractor[];
};

export interface BaseManager {
  id: string;
  email?: string;
  fullName: string;
  avatarUrl: string;
  zohoId: string;
  legalLocation?: string;
  city?: string;
  designation?: string;
  stream?: string;
}

export interface BaseProject {
  id: string;
  name: string;
}

export interface HeadcountSummary {
  projectedHeadcount: number;
  projectedHeadcountStartDate: string;
  projectedJoiningHeadcount: number;
  projectedLeavingHeadcount: number;
}

export interface ProjectOverview extends BaseProject {
  membersCount: number;
  grossMargin?: number | null;
  netMargin?: number | null;
  pastRevenue?: number | null;
  projectedRevenue?: number | null;
  projectedRevenueLogs?: ProjectedRevenuePayCostLog[] | null;
  revenueLastInvoiceDate?: string | null;
  expectedMembersCount: number;
  startedAt: string | null;
  endedAt: string | null;
  membersWithSowToExpireWithinMonth: number;
  sowStatus: SowStatus | null;
  latestScopeAssessment?: ScopeTimelineAssessment;
  latestTimelineAssessment?: ScopeTimelineAssessment;
  latestGeneralAssessment?: GeneralAssessment;
  initialRevenue?: number | null;
}

export interface ProjectReport extends BaseProject {
  membersCount: number;
  members?: Member[];
}

export type BaseProjectDetail = Omit<
  ProjectOverview,
  | 'expectedMembersCount'
  | 'startedAt'
  | 'endedAt'
  | 'membersWithSowToExpireWithinMonth'
  | 'sowStatus'
> & {
  grossMarginTargets?: MarginRange;
  netMarginTargets?: MarginRange;
  grossMarginHighlights?: MarginHighlight[];
  netMarginHighlights?: MarginHighlight[];
  clientPartner?: BaseManager;
  portfolioDeliveryManager?: BaseManager;
  deliveryManager?: BaseManager;
};

export type ProjectDetail = BaseProjectDetail & {
  members?: Member[] | [];
  headcountSummary?: HeadcountSummary;
  projectedRevenueEndDate?: string;
  operationalCost?: OperationalCost[];
  pastRevenueCurrentYear?: number | null;
} & FinanceStats;

export interface DummmyProjectDetail extends BaseProjectDetail {
  members?: DummyMember[] | [];
}

export interface FlagsCount {
  red: number;
  yellow: number;
}

export interface CustomLinks {
  accountId?: string;
  id?: string;
  title: string;
  url: string;
}

export interface AccountOverview extends BaseAccount {
  operationalCost?: OperationalCost[];
  cpFullName?: string;
  pdmFullName?: string;
  dmFullName?: string;
  cpId?: string;
  pdmId?: string;
  dmId?: string;
  clientPartner?: BaseManager;
  portfolioDeliveryManager?: BaseManager;
  deliveryManager?: BaseManager;
  projects: ProjectOverview[];
  zohoId?: string;
  csat?: CsatValues;
  sharedDriveLink: string | null;
  status: AccountStatus;
  flagsCount?: FlagsCount;
  openPositions?: number;
  customLinks: CustomLinks[] | [];
  compensationToolLink?: string;
}

export interface AccountOverviewWithAssessments extends AccountOverview {
  latestTimelineAssessmentScore?: number;
  latestScopeAssessmentScore?: number;
  latestGeneralAssessmentScore?: number;
}

export interface Margins {
  grossMargin: number;
  netMargin: number;
  newGrossMargin: number;
  newNetMargin: number;
}

export interface FinanceStats extends Margins {
  pastPayCost: number;
  pastOperationalCost: number;
  pastRevenue: number;
  projectedPayCost: number;
  projectedOperationalCost: number;
  projectedRevenue: number;
  projectedRevenueLogs: ProjectedRevenuePayCostLog[];
  projectedOperationalCostLogs: ProjectedOperationalCostLog[];
}

export type AccountDetail = Omit<AccountOverview, 'projects'> & {
  projects: ProjectDetail[];
  grossMarginTargets?: MarginRange;
  netMarginTargets?: MarginRange;
  grossMarginHighlights?: MarginHighlight[];
  netMarginHighlights?: MarginHighlight[];
  headcountSummary?: HeadcountSummary;
  pastRevenueCurrentYear?: number | null;
  currentYear?: number;
} & FinanceStats;

export interface CsatValues {
  all?: number;
  last6Months?: number;
  last3Months?: number;
  latest?: number;
}

export interface LegalLocationType {
  id?: string;
  country?: LegalLocation;
  employmentType?: EmploymentType;
  countryCode?: string;
}

export enum SowStatus {
  // if startDate <= current date <= endDate
  Active = 'Active',
  // if current date < startDate
  Potential = 'Potential',
  // if endDate < current date
  Expired = 'Expired',
}

export enum AccountStatus {
  Active = 'Active',
  Closed = 'Closed',
}

export interface HeadcountChange {
  tempId?: string;
  id?: string;
  tempProjectId?: string;
  projectId: string | null;
  blendedRate: number;
  blendedPayRate?: number | null;
  countChange: number;
  date: string;
  delete?: boolean;
}

export interface PutProjectResponseDto {
  headcountChanges: HeadcountChange[];
  financialStats: {
    [projectId: string]: {
      projectedRevenue: number;
    };
  };
}

export interface OperationalCost {
  id?: string;
  operationalCost: number;
  period: number;
}

// --- --- --- --- --- resources types from FSS --- --- --- --- ---
export interface CreateResourceDto {
  projectId: string;
  userId: string;
  seniority: Seniority;
  position: string;
  billRate: number;
  allocation: number;
  startedAt: string | null;
  endedAt: string | null;
  status: ResourceStatus;
}

export interface ResourceDto
  extends CreateResourceDto,
    TimestampsFields,
    IdField {}

export type ResourceFullDto = ResourceDto;

export type ResourceEditableFieldsDto = Pick<
  ResourceFullDto,
  'seniority' | 'position' | 'billRate' | 'allocation' | 'startedAt' | 'endedAt'
>;

// --- --- --- --- --- accounts and projects types from FSS --- --- --- --- ---
export enum ExtOperType {
  CHANGE_PROJECT_IN_ZP = 'CHANGE_PROJECT_IN_ZP',
  APPEND_TO_SHEET = 'APPEND_TO_SHEET',
}

export enum ExtOperStatus {
  // COMMON
  SUCCESS = 'SUCCESS',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  // For CHANGE_PROJECT_IN_ZP
  NO_OLD_OR_NEW_PROJECT = 'NO_OLD_OR_NEW_PROJECT',
  NO_EMPTY_SLOT = 'NO_EMPTY_SLOT',
  // For APPEND_TO_SHEET
}

export enum ResourceStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum BillingType {
  ONCE = 'once',
  TWICE = 'twice',
  FIXED = 'fixed',
}

export interface GetAccountsAdminResponse {
  accounts: AccountAdminDto[];
}

export interface AccountAdminDto {
  id: string;
  name: string;
  departmentName?: string | null;
  revenueLastInvoiceDate?: string | null;
  pastRevenue?: number | null;
  projects: ProjectAdminDto[];
  pendingRequestsCount?: number;
  pdmId?: string;
  pdmFullName?: string;
  dmId?: string;
  dmFullName?: string;
  status?: string;
}

export interface ProjectAdminDto {
  id: string;
  name: string;
  originalFullName: string;
  isFoundInZp: boolean;
  accountId: string;
  billingType: BillingType;
  pastRevenue: number | null;
  revenueLastInvoiceDate: string | null;
  projectedRevenueEndDate: string | null;
  sowStatus: SowStatus | null;
  membersWithSowToExpireWithinMonth: number;
  members: UserForAdminDto[];
  pendingRequestsCount?: number;
}
export interface ProjectDropdownDto {
  id: string;
  name: string;
  originalFullName: string;
  accountId: string;
}

export interface AccountDropdownDto {
  id: string;
  name: string;
}

export interface ProjectSearchDto {
  id: string;
  name: string;
  accountId: string;
}

export interface UserForAdminDto {
  id: string;
  resourceId: string;
  fullName: string;
  designation: string;
  seniority: Seniority;
  billRate: number;
  allocation: number;
  startedAt: string | null;
  endedAt: string | null;
  status: ResourceStatus;
  avatarUrl: string | null;
  isDeleteReqPending?: boolean;
  isUpdateReqPending?: boolean;
}

export interface PutResourceDto {
  resourceId: string;
  newProjectId: string;
  oldProjectId: string;
  notes?: string | null;
  newEndedAt?: string | null;
  newStartedAt: string;
  position: SowStream;
  seniority: Seniority;
  billRate: number;
  allocation: number;
  prevResourceAction:
    | {
        action: 'deactivate';
      }
    | {
        action: 'nothing';
      }
    | {
        action: 'modify';
        oldEndedAt?: string;
        oldAllocation?: number;
      };

  files?: File[];
}

export interface EditResourceDto {
  resourceId: string;
  seniority?: Seniority;
  position?: string;
  billRate?: number;
  allocation?: number;
  startedAt?: string;
  endedAt?: string;
  notes?: string;

  files?: File[];
}

export const DEACTIVATION_REASONS = [
  'Project ended',
  'Contractor left AE',
  'Offboarded from project',
] as const;

export type DeactivationReason = typeof DEACTIVATION_REASONS[number];

export interface DeactivateResourceDto {
  resourceId: string;
  endedAt?: string | null;
  deactivationReason: DeactivationReason;
  notes: string;
  files?: File[];
}

export interface DeactivateMultipleResourceDto {
  resourceIds: string[];
  endedAt?: string | null;
  deactivationReason: DeactivationReason;
  notes?: string;
  files?: File[];
}

// --- --- --- --- --- Alert Types --- --- --- --- ---
export enum AlertCategory {
  CLIENT_FINANCIAL_ISSUES = 'CLIENT_FINANCIAL_ISSUES',
  IMPORTANT_CLIENT_STAKEHOLDER_CHANGES = 'IMPORTANT_CLIENT_STAKEHOLDER_CHANGES',
  AE_TEAM_PERFORMANCE_ISSUES = 'AE_TEAM_PERFORMANCE_ISSUES',
  PROJECT_DELIVERY_ISSUES = 'PROJECT_DELIVERY_ISSUES',
  AE_TEAM_ATTRITION = 'AE_TEAM_ATTRITION',
  CHALLENGES_IN_TEAM_BUILD_UP_SLOW_RECRUITING = 'CHALLENGES_IN_TEAM_BUILD_UP_SLOW_RECRUITING',
  CLIENT_STRATEGY_CHANGE = 'CLIENT_STRATEGY_CHANGE',
  THE_END_OF_PROJECT = 'THE_END_OF_PROJECT',
  OTHER = 'OTHER',
}

export const CategoryLabels = {
  [AlertCategory.CLIENT_FINANCIAL_ISSUES]: 'Client financial issues',
  [AlertCategory.IMPORTANT_CLIENT_STAKEHOLDER_CHANGES]:
    'Important client stakeholder changes',
  [AlertCategory.AE_TEAM_PERFORMANCE_ISSUES]: 'AE team performance issues',
  [AlertCategory.PROJECT_DELIVERY_ISSUES]: 'Project delivery issues',
  [AlertCategory.AE_TEAM_ATTRITION]: 'AE team attrition',
  [AlertCategory.CHALLENGES_IN_TEAM_BUILD_UP_SLOW_RECRUITING]:
    'Challenges in the team build-up/slow recruiting',
  [AlertCategory.CLIENT_STRATEGY_CHANGE]: 'Client strategy change',
  [AlertCategory.THE_END_OF_PROJECT]: 'The end of project',
  [AlertCategory.OTHER]: 'Other',
};

export enum AlertResolvedStatus {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
}

export const ResolvedStatusLabel = {
  [AlertResolvedStatus.POSITIVE]: 'Positive',
  [AlertResolvedStatus.NEGATIVE]: 'Negative',
};

export interface AlertType {
  id: string;

  name: string;
  categories: AlertCategory[];

  etaForResolve: string;
  problemStatement: string;
  whatWasDone: string;
  actionPlan: string;
  risksAndBlockers: string;

  ownerId: string;
  accountId: string;
  account: { id: string; name: string };

  resolvedStatus: AlertResolvedStatus | null;
  resolvedSummary: string | null;
  resolvedAt: string | null;

  owner: UserWithPermissions | null;
  authorId: string;
  alertEmailRecipientsEmails: string[];

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface UpsertAlertType
  extends Pick<
    AlertType,
    | 'name'
    | 'categories'
    | 'problemStatement'
    | 'whatWasDone'
    | 'actionPlan'
    | 'risksAndBlockers'
    | 'accountId'
    | 'resolvedStatus'
    | 'resolvedSummary'
    | 'resolvedAt'
    | 'owner'
    | 'authorId'
    | 'alertEmailRecipientsEmails'
  > {
  ownerId: string | null;
  etaForResolve: string;
}

export interface ResolveAlertType {
  resolvedStatus: AlertResolvedStatus;
  resolvedSummary: string;
  id: string;
}
